import { Box, InputAdornment, TextField } from '@mui/material';

import CustomLabel from '../../../ui-components/CustomLabel';
import { getFlagUrlByIso, getFlagUrl } from 'utilities/countryFlags';

// Shared field primitives for onboarding step 1 and 2. These live here rather
// than in index.js so the location fields can be rendered - and tested - on
// their own, without standing up the whole onboarding screen.
// ---------------------------------------------------------------------------
// Design tokens (matched to the provided mockups)
// ---------------------------------------------------------------------------
export const INDIGO = '#4F46E5';
export const INDIGO_SOFT = '#EEF0FF';
export const ICON_COLOR = '#6366F1';
export const BORDER = '#E4E7EC';
export const BORDER_HOVER = '#C7CAD1';
export const LABEL_COLOR = '#344054';
export const PLACEHOLDER = '#98A2B3';
export const TEXT_COLOR = '#101828';

export const fieldSx = {
  '& .MuiFilledInput-root': {
    backgroundColor: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'border-color .15s ease, box-shadow .15s ease',
    '&:hover': { backgroundColor: '#fff', borderColor: BORDER_HOVER },
    // '&.Mui-focused': {
    //   backgroundColor: '#fff',
    //   borderColor: ICON_COLOR,
    //   boxShadow: '0 0 0 4px rgba(99,102,241,0.12)',
    // },
    '&.Mui-error': { borderColor: '#F04438' },
  },
  '& .MuiFilledInput-input': {
    padding: '15px 14px',
    fontSize: '0.95rem',
    color: TEXT_COLOR,
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #fff inset',
      WebkitTextFillColor: TEXT_COLOR,
    },
    // '&:-webkit-autofill:focus': {
    //   WebkitBoxShadow: '0 0 0 1000px #fff inset',
    //   WebkitTextFillColor: TEXT_COLOR,
    // },
  },
  '& .MuiFilledInput-input::placeholder': { color: PLACEHOLDER, opacity: 1 },
  '& .MuiInputAdornment-root': {
    marginTop: '0 !important',
    marginLeft: '12px',
  },
};

// Resolve a country flag image URL: prefer the ISO2 code from the API, and
// fall back to a name-based lookup from the country flag config.
export const flagFor = (country) =>
  getFlagUrlByIso(country?.iso2) || getFlagUrl(country?.label);

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
// Uses the shared CustomLabel (title-casing + required asterisk) while keeping
// onboarding-v2's own label styling and the "(Optional)" affordance.
export const FieldLabel = ({ children, required, optional, ...props }) => (
  <CustomLabel
    required={required}
    sx={
      {
        // marginBottom: '8px',
        // fontSize: '0.8rem',
        // fontWeight: 600,
        // letterSpacing: '0.02em',
        // color: LABEL_COLOR,
      }
    }
    {...props}
  >
    {children}
    {optional && (
      <span style={{ color: PLACEHOLDER, fontWeight: 400, marginLeft: 6 }}>
        (Optional)
      </span>
    )}
  </CustomLabel>
);

export const TextInput = ({
  icon: Icon,
  error,
  prefix,
  value,
  onChange,
  extraSx = {},
  ...props
}) => (
  <TextField
    fullWidth
    variant="filled"
    autoComplete="off"
    error={!!error}
    helperText={
      error ? (
        <span style={{ color: '#F04438', fontSize: '0.75rem' }}>{error}</span>
      ) : (
        ''
      )
    }
    value={value}
    onChange={(e) => {
      if (onChange) {
        const trimmedValue = e.target.value.trimStart();
        onChange({ ...e, target: { ...e.target, value: trimmedValue } });
      }
    }}
    sx={{ ...fieldSx, ...extraSx }}
    InputProps={{
      disableUnderline: true,
      startAdornment:
        Icon || prefix ? (
          <InputAdornment position="start">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ...(prefix && {
                  pr: 1.2,
                  borderRight: `1px solid ${BORDER}`,
                }),
              }}
            >
              {Icon && <Icon size={18} color={ICON_COLOR} strokeWidth={1.7} />}
              {prefix && (
                <Box
                  component="span"
                  sx={{
                    color: TEXT_COLOR,
                    fontWeight: 500,
                    fontSize: '0.95rem',
                  }}
                >
                  {prefix}
                </Box>
              )}
            </Box>
          </InputAdornment>
        ) : null,
    }}
    {...props}
  />
);

// Lines an Autocomplete's filled input up with the plain TextInput fields
// above it (height, icon offset, text baseline).
export const autocompleteSlotSx = {
  '& .MuiFilledInput-root': {
    backgroundColor: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: '12px',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    paddingLeft: '0 !important',
    '&:hover': { backgroundColor: '#fff', borderColor: BORDER_HOVER },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      borderColor: BORDER_HOVER,
      // boxShadow: '0 0 0 4px rgba(99,102,241,0.12)',
    },
    '&:before, &:after': { display: 'none' },
  },
  '& .MuiFilledInput-input': {
    padding: '15px 14px !important',
    fontSize: '0.95rem',
    color: TEXT_COLOR,
  },
  '& .MuiFilledInput-input::placeholder': { color: PLACEHOLDER, opacity: 1 },
  '& .MuiInputAdornment-root': {
    marginTop: '0 !important',
    marginLeft: '12px',
  },
};
